import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { Role } from '@ivario-arc/shared/src/enums';

interface ImpersonateContextType {
  isImpersonating: boolean;
  impersonateSampleId: string | null;
  canImpersonate: boolean;
}

const ImpersonateContext = createContext<ImpersonateContextType | undefined>(
  undefined,
);

interface ImpersonateProviderProps {
  children: ReactNode;
}

export function ImpersonateProvider({ children }: ImpersonateProviderProps) {
  const [searchParams] = useSearchParams();
  const auth = useAuth();

  const contextValue = useMemo(() => {
    const groups = (auth.user?.profile?.groups as string[] | undefined) ?? [];
    const canImpersonate = groups.includes(Role.ADMIN);
    const impersonateSampleId = searchParams.get('impersonate');
    const isImpersonating = Boolean(impersonateSampleId && canImpersonate);

    return {
      isImpersonating,
      impersonateSampleId: isImpersonating ? impersonateSampleId : null,
      canImpersonate,
    };
  }, [searchParams, auth.user?.profile?.groups]);

  return (
    <ImpersonateContext.Provider value={contextValue}>
      {children}
    </ImpersonateContext.Provider>
  );
}

export function useImpersonate() {
  const context = useContext(ImpersonateContext);
  if (context === undefined) {
    throw new Error(
      'useImpersonate must be used within an ImpersonateProvider',
    );
  }
  return context;
}
